import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

const WsCtx = createContext(null);

// Convert REACT_APP_BACKEND_URL into the matching ws(s):// origin
function wsOrigin() {
  const u = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return u.replace(/^http/i, "ws");
}

export function WebSocketProvider({ children }) {
  const { user, token } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);
  const subscribersRef = useRef(new Map()); // event -> Set<callback>
  const [status, setStatus] = useState("disconnected"); // disconnected | connecting | connected
  const [lastEvent, setLastEvent] = useState(null);

  const signedIn = user && user.email;
  const authToken = token || (typeof window !== "undefined" ? localStorage.getItem("e65_token") : null);

  const subscribe = useCallback((event, cb) => {
    const set = subscribersRef.current.get(event) || new Set();
    set.add(cb);
    subscribersRef.current.set(event, set);
    return () => {
      const s = subscribersRef.current.get(event);
      if (s) {
        s.delete(cb);
        if (s.size === 0) subscribersRef.current.delete(event);
      }
    };
  }, []);

  const dispatch = useCallback((event, data) => {
    const set = subscribersRef.current.get(event);
    if (set) set.forEach((cb) => { try { cb(data); } catch (e) { console.error("WS handler", e); } });
    const all = subscribersRef.current.get("*");
    if (all) all.forEach((cb) => { try { cb({ event, data }); } catch { /* ignore */ } });
  }, []);

  const send = useCallback((obj) => {
    try {
      if (wsRef.current && wsRef.current.readyState === 1) {
        wsRef.current.send(JSON.stringify(obj));
        return true;
      }
    } catch (e) { console.warn("WS send err", e); }
    return false;
  }, []);

  useEffect(() => {
    if (!signedIn || !authToken) {
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* ignore */ } }
      wsRef.current = null;
      setStatus("disconnected");
      return;
    }
    let alive = true;

    const connect = () => {
      if (!alive) return;
      try {
        const url = `${wsOrigin()}/api/ws?token=${encodeURIComponent(authToken)}`;
        setStatus("connecting");
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => {
          setStatus("connected");
          // Heartbeat ping every 25s to keep ingress alive
          pingTimer.current = setInterval(() => {
            try { ws.send(JSON.stringify({ type: "ping" })); } catch { /* ignore */ }
          }, 25000);
        };
        ws.onmessage = (ev) => {
          try {
            const parsed = JSON.parse(ev.data);
            setLastEvent(parsed);
            dispatch(parsed.event, parsed.data ?? parsed);
          } catch { /* ignore */ }
        };
        ws.onerror = () => {};
        ws.onclose = () => {
          setStatus("disconnected");
          if (pingTimer.current) { clearInterval(pingTimer.current); pingTimer.current = null; }
          if (alive) {
            reconnectTimer.current = setTimeout(connect, 4000);
          }
        };
      } catch {
        setStatus("disconnected");
        if (alive) reconnectTimer.current = setTimeout(connect, 4000);
      }
    };

    connect();

    return () => {
      alive = false;
      if (pingTimer.current) clearInterval(pingTimer.current);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        try { wsRef.current.close(); } catch { /* ignore */ }
      }
      wsRef.current = null;
    };
  }, [signedIn, authToken, dispatch]);

  return (
    <WsCtx.Provider value={{ status, lastEvent, subscribe, send }}>
      {children}
    </WsCtx.Provider>
  );
}

export const useWebSocket = () => {
  const ctx = useContext(WsCtx);
  if (!ctx) return { status: "disconnected", subscribe: () => () => {}, send: () => false, lastEvent: null };
  return ctx;
};

/** Hook helper: subscribe to a single event and re-subscribe on dep change. */
export function useWsEvent(event, handler, deps = []) {
  const { subscribe } = useWebSocket();
  useEffect(() => {
    if (!event || !handler) return undefined;
    const unsub = subscribe(event, handler);
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, ...deps]);
}

/** Hook helper: returns the raw `send` function for ad-hoc WS messages. */
export function useWsSend() {
  const { send } = useWebSocket();
  return send;
}
